<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯客戶' : '新增客戶' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel" :disabled="loading">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="loading" @click="handleSubmit">
          <span v-if="loading">{{ isEdit ? '更新中...' : '建立中...' }}</span>
          <span v-else>{{ isEdit ? '更新客戶' : '建立客戶' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-client-container">

    <div class="form-container">
      <!-- 編輯模式 Tab 導航 -->
      <div v-if="isEdit" class="edit-tabs">
        <button type="button" class="tab-btn" :class="{ active: editTab === 'basic' }" @click="editTab = 'basic'">
          <i class="ri-file-list-3-line"></i>
          基本資訊
        </button>
        <button type="button" class="tab-btn" :class="{ active: editTab === 'line' }" @click="editTab = 'line'">
          <i class="ri-chat-smile-2-line"></i>
          LINE 設定
        </button>
        <button type="button" class="tab-btn" :class="{ active: editTab === 'modules' }" @click="editTab = 'modules'">
          <i class="ri-puzzle-line"></i>
          選購模組
        </button>
        <button v-if="isSystemAccount" type="button" class="tab-btn" :class="{ active: editTab === 'api' }" @click="editTab = 'api'">
          <i class="ri-key-2-line"></i>
          API 憑證
        </button>
        <button type="button" class="tab-btn" :class="{ active: editTab === 'sms' }" @click="openSmsTab">
          <i class="ri-message-2-line"></i>
          簡訊點數
        </button>
      </div>

      <form @submit.prevent="handleSubmit">
        <!-- 基本資訊 -->
        <div v-show="!isEdit || editTab === 'basic'">
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-row">
            <div class="form-group" v-if="isSystemAccount">
              <label for="platformId" class="required">平台</label>
              <select
                id="platformId"
                v-model="form.platformId"
                class="form-control"
                :class="{ 'is-invalid': errors.platformId }"
                :disabled="isEdit || loadingPlatforms"
                required
              >
                <option value="">請選擇平台</option>
                <option v-for="platform in platforms" :key="platform.id" :value="platform.id">
                  {{ platform.name }} ({{ platform.code }})
                </option>
              </select>
              <div v-if="errors.platformId" class="invalid-feedback">
                {{ errors.platformId }}
              </div>
              <p v-if="isEdit" class="form-hint">平台一旦建立後無法修改</p>
            </div>
            <div v-else-if="isPlatformAccount" class="form-group">
              <label>平台</label>
              <input
                type="text"
                class="form-control"
                :value="currentPlatformName"
                disabled
              />
              <p class="form-hint">平台帳號只能為自己的平台創建客戶</p>
            </div>

            <div class="form-group">
              <label for="clientCode" class="required">客戶代碼</label>
              <div class="input-with-button">
                <input
                  id="clientCode"
                  v-model="form.clientCode"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.clientCode }"
                  placeholder="請輸入客戶代碼（唯一識別碼）"
                  :disabled="isEdit"
                  required
                />
                <button
                  v-if="!isEdit"
                  type="button"
                  class="btn btn-generate"
                  @click="handleGenerateCode"
                  :disabled="loadingGenerate || !form.platformId"
                  title="系統自動生成客戶代碼"
                >
                  <i class="ri-refresh-line" :class="{ 'spinning': loadingGenerate }"></i>
                  {{ loadingGenerate ? '生成中...' : '系統生成' }}
                </button>
              </div>
              <div v-if="errors.clientCode" class="invalid-feedback">
                {{ errors.clientCode }}
              </div>
              <p class="form-hint">客戶代碼一旦建立後無法修改</p>
              <p v-if="!isEdit" class="form-hint">
                <i class="ri-information-line"></i>
                系統生成規則：8碼數字，前3碼為民國年，後5碼為當年度遞增編號（00000 隨機+3~9）
              </p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="clientName" class="required">客戶名稱</label>
              <input
                id="clientName"
                v-model="form.clientName"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.clientName }"
                placeholder="請輸入客戶名稱"
                required
              />
              <div v-if="errors.clientName" class="invalid-feedback">
                {{ errors.clientName }}
              </div>
            </div>

            <div class="form-group">
              <label for="planId">訂閱方案</label>
              <select
                id="planId"
                v-model="form.planId"
                class="form-control"
                :disabled="loadingPlans"
              >
                <option value="">無（暫不指定方案）</option>
                <option v-for="plan in plans" :key="plan.id" :value="plan.id">
                  {{ plan.planName }} ({{ plan.planCode }})
                </option>
              </select>
              <p class="form-hint">
                指定客戶使用的方案，影響可用的功能模組<br>
                <small>⚠️ 注意：此列表只顯示啟用的方案，停用的方案無法被指定</small>
              </p>
            </div>
          </div>

          <div v-if="isSystemAccount" class="form-row">
            <div class="form-group">
              <label class="perpetual-check" for="subscriptionNeverExpires">
                <input
                  id="subscriptionNeverExpires"
                  v-model="form.subscriptionNeverExpires"
                  type="checkbox"
                />
                訂閱永久有效
              </label>
              <p class="form-hint">
                適用於系統開發人員與合作經銷商。開啟後不受訂閱到期、寬限期與到期提醒限制，功能可繼續使用。仍需指定方案，選單才會出現對應模組。關閉後，若到期日已過，每日檢查會重新進入寬限期。
              </p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="industry">行業</label>
              <input
                id="industry"
                v-model="form.industry"
                type="text"
                class="form-control"
                placeholder="請輸入行業（例如：餐飲、零售等）"
              />
            </div>
            <div class="form-group">
              <label for="status">狀態</label>
              <select
                id="status"
                v-model="form.status"
                class="form-control"
              >
                <option value="active">啟用</option>
                <option value="inactive">停用</option>
                <option value="suspended">暫停</option>
              </select>
              <p class="form-hint">停用的客戶將無法使用</p>
            </div>

            <div class="form-group">
              <label for="memberSystemType" class="required">會員系統類型</label>
              <select
                id="memberSystemType"
                v-model="form.memberSystemType"
                class="form-control"
                :class="{ 'is-invalid': errors.memberSystemType }"
              >
                <option value="LOCKLINE">LOCKLINE 會員系統</option>
                <option value="WIXTAR">Wixtar 會員系統</option>
              </select>
              <div v-if="errors.memberSystemType" class="invalid-feedback">
                {{ errors.memberSystemType }}
              </div>
              <p class="form-hint">
                <span v-if="form.memberSystemType === 'LOCKLINE'">
                  使用本平台的會員、點數、票券系統
                </span>
                <span v-else>
                  使用 Wixtar 的會員、點數、票券系統，本系統資料為副本（透過 Webhook 同步）
                </span>
              </p>
              <p v-if="isEdit" class="form-hint text-warning">
                ⚠️ 變更會員系統類型可能導致資料不一致，請謹慎操作。從 LOCKLINE 變更為 WIXTAR 時，請確保已設定 Wixtar 配置。
              </p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="billingMethod">付款方式</label>
              <select
                id="billingMethod"
                v-model="form.billingMethod"
                class="form-control"
              >
                <option value="manual_billing">人工請款</option>
                <option value="self_subscription">自行訂閱</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="maxStores">門市數上限</label>
              <input
                id="maxStores"
                v-model.number="form.maxStores"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.maxStores }"
                placeholder="請輸入門市數上限（留空則不限制）"
                min="1"
              />
              <div v-if="errors.maxStores" class="invalid-feedback">
                {{ errors.maxStores }}
              </div>
              <p class="form-hint">客戶可以管理的門市數量上限，留空則不限制</p>
              <div class="plan-limit-box">目前方案：<strong>{{ planLimitDisplay.maxStores }}</strong></div>
            </div>

            <div class="form-group">
              <label for="maxMembers">會員數上限</label>
              <input
                id="maxMembers"
                v-model.number="form.maxMembers"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.maxMembers }"
                placeholder="請輸入會員數上限（留空則不限制）"
                min="1"
              />
              <div v-if="errors.maxMembers" class="invalid-feedback">
                {{ errors.maxMembers }}
              </div>
              <p class="form-hint">客戶可以管理的會員數量上限，留空則不限制</p>
              <div class="plan-limit-box">目前方案：<strong>{{ planLimitDisplay.maxMembers }}</strong></div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="maxLineAccounts">LINE 官方帳號上限</label>
              <input
                id="maxLineAccounts"
                v-model.number="form.maxLineAccounts"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.maxLineAccounts }"
                placeholder="請輸入 LINE 官方帳號數量上限（留空則不限制）"
                min="1"
              />
              <div v-if="errors.maxLineAccounts" class="invalid-feedback">
                {{ errors.maxLineAccounts }}
              </div>
              <p class="form-hint">該客戶可綁定的 LINE 官方帳號數量上限，留空則不限制</p>
              <div class="plan-limit-box">目前方案：<strong>{{ planLimitDisplay.maxLineAccounts }}</strong></div>
            </div>

            <div class="form-group">
              <label for="maxLineFriends">好友數上限</label>
              <input
                id="maxLineFriends"
                v-model.number="form.maxLineFriends"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.maxLineFriends }"
                placeholder="請輸入好友數上限（留空則不限制）"
                min="1"
              />
              <div v-if="errors.maxLineFriends" class="invalid-feedback">
                {{ errors.maxLineFriends }}
              </div>
              <p class="form-hint">LINE 官方帳號好友數量上限（可為單一帳號或合計，依業務規則），留空則不限制</p>
              <div class="plan-limit-box">目前方案：<strong>{{ planLimitDisplay.maxLineFriends }}</strong></div>
            </div>
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>
        </div><!-- end basic tab wrapper -->

        <!-- LINE 設定 Tab -->
        <div v-if="isEdit && editTab === 'line'" class="form-section">
          <h3 class="section-title">LINE 設定</h3>
          <div v-if="loadingLineSettings" class="loading-state">
            <i class="ri-loader-4-line spin"></i>
            <span>載入中...</span>
          </div>
          <template v-else>
            <div class="form-group">
              <label class="form-label">跨 OA 會員共用模式</label>
              <p class="form-hint" style="margin-bottom: 12px;">
                當客戶擁有多個同一 LINE Provider 下的官方帳號時，決定會員加入新 OA 時的綁定方式。
              </p>
              <div class="line-binding-mode-options">
                <label
                  class="mode-option-label"
                  :class="{ active: lineSettings.crossOaBindingMode === 'phone_verification' }"
                  @click="lineSettings.crossOaBindingMode = 'phone_verification'"
                >
                  <span class="mode-radio-dot" :class="{ checked: lineSettings.crossOaBindingMode === 'phone_verification' }"></span>
                  <span class="mode-text">
                    <strong>電話驗證共用（預設）</strong>
                    <span class="mode-desc">會員加入新 OA 好友後，需自行開啟 LIFF 並輸入手機號碼完成綁定。</span>
                  </span>
                </label>
                <label
                  class="mode-option-label"
                  :class="{ active: lineSettings.crossOaBindingMode === 'auto' }"
                  @click="lineSettings.crossOaBindingMode = 'auto'"
                >
                  <span class="mode-radio-dot" :class="{ checked: lineSettings.crossOaBindingMode === 'auto' }"></span>
                  <span class="mode-text">
                    <strong>靜默共用</strong>
                    <span class="mode-desc">加好友當下自動繼承既有會員綁定，無需任何額外操作。</span>
                  </span>
                </label>
              </div>
            </div>
            <div class="form-actions-inline">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="lineSettingsSaving"
                @click="handleSaveLineSettings"
              >
                <span v-if="lineSettingsSaving">儲存中...</span>
                <span v-else>儲存 LINE 設定</span>
              </button>
            </div>
          </template>
        </div>

        <!-- 選購模組 Tab -->
        <div v-if="isEdit && editTab === 'modules'" class="form-section">
          <h3 class="section-title">選購模組管理</h3>
          <div class="optional-modules-section">
            <p class="section-description">
              為客戶添加選購模組，這些模組會額外計費並顯示在客戶的選單中。
            </p>
            
            <!-- 已選購模組列表 -->
            <div v-if="loadingOptionalModules" class="loading-state">
              <i class="ri-loader-4-line spin"></i>
              <span>載入中...</span>
            </div>
            <div v-else-if="optionalModules.length > 0" class="optional-modules-list">
              <h4 class="modules-list-title">已選購模組（{{ optionalModules.length }}）</h4>
              <div class="modules-grid">
                <div
                  v-for="module in optionalModules"
                  :key="module.id"
                  class="module-card"
                >
                  <div class="module-header">
                    <h5 class="module-name">{{ module.module_name }}</h5>
                    <span class="module-code">{{ module.module_code }}</span>
                  </div>
                  <div class="module-details">
                    <div class="detail-item">
                      <span class="detail-label">計費週期：</span>
                      <span class="detail-value">{{ module.billing_cycle === 'yearly' ? '年繳' : '月繳' }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">{{ module.billing_cycle === 'yearly' ? '年費' : '月費' }}：</span>
                      <span class="detail-value">NT$ {{ formatCurrency(module.billing_cycle === 'yearly' ? (module.default_yearly_price || 0) : (module.monthly_price || module.default_monthly_price || 0)) }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">開始日期：</span>
                      <span class="detail-value">{{ formatDate(module.start_date) }}</span>
                    </div>
                    <div v-if="module.end_date" class="detail-item">
                      <span class="detail-label">結束日期：</span>
                      <span class="detail-value">{{ formatDate(module.end_date) }}</span>
                    </div>
                    <div v-else class="detail-item">
                      <span class="detail-label">狀態：</span>
                      <span class="detail-value">持續計費中</span>
                    </div>
                    <div v-if="module.module_code === 'CHATDESK'" class="detail-item">
                      <span class="detail-label">座席：</span>
                      <span class="detail-value">
                        內含 {{ module.seats?.includedSeats ?? 3 }} ＋ 加購 {{ module.seats?.extraSeats ?? 0 }}
                        ＝ 共 {{ module.seats?.totalSeats ?? ((module.seats?.includedSeats ?? 3) + (module.seats?.extraSeats ?? 0)) }} 席
                      </span>
                    </div>
                    <div
                      v-if="module.module_code === 'CHATDESK' && module.seats?.pendingExtraSeats != null"
                      class="detail-item"
                    >
                      <span class="detail-label">待生效減席：</span>
                      <span class="detail-value" style="color:#b45309">
                        加購改為 {{ module.seats.pendingExtraSeats }} 席，
                        {{ formatDate(module.seats.pendingSeatsEffectiveAt) }} 生效
                      </span>
                    </div>
                  </div>
                  <div class="module-actions">
                    <button
                      v-if="module.module_code === 'CHATDESK' && !module.end_date"
                      type="button"
                      class="btn btn-secondary btn-sm"
                      @click="openSeatsModal(module)"
                    >
                      <i class="ri-user-add-line"></i>
                      設定座席
                    </button>
                    <button
                      v-if="!module.end_date"
                      type="button"
                      class="btn btn-warning btn-sm"
                      @click="handleCancelModule(module)"
                      :disabled="cancellingModuleId === module.module_id"
                    >
                      <i class="ri-close-circle-line"></i>
                      {{ cancellingModuleId === module.module_id ? '取消中...' : '取消' }}
                    </button>
                    <span v-else class="cancelled-badge">已取消</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <i class="ri-inbox-line"></i>
              <p>尚未添加任何選購模組</p>
            </div>
            
            <button
              type="button"
              class="btn btn-primary"
              @click="goToOptionalModules"
            >
              <i class="ri-shopping-cart-line"></i>
              管理選購模組
            </button>
          </div>
        </div>

        <!-- API 憑證 Tab -->
        <div v-if="isEdit && editTab === 'api' && isSystemAccount" class="form-section">
          <h3 class="section-title">API 憑證管理（合作平台使用）</h3>
          <div class="api-credentials-section">
            <p class="section-description">
              提供 API Key 和 Secret 給合作平台，讓合作平台可以代表此客戶呼叫 API，無需客戶的登入帳密。
            </p>
            
            <!-- 憑證資訊顯示 -->
            <div v-if="loadingCredential" class="loading-state">
              <i class="ri-loader-4-line spin"></i>
              <span>載入憑證資訊中...</span>
            </div>
            <div v-else-if="apiCredential" class="credential-info">
              <div class="credential-field">
                <label>API Key：</label>
                <div class="credential-value">
                  <code>{{ apiCredential.apiKey }}</code>
                  <button
                    type="button"
                    class="btn-copy"
                    @click="copyToClipboard(apiCredential.apiKey, 'API Key')"
                    title="複製 API Key"
                  >
                    <i class="ri-file-copy-line"></i>
                  </button>
                </div>
              </div>
              <div class="credential-field" v-if="apiCredential.apiSecret">
                <label>API Secret：</label>
                <div class="credential-value">
                  <code>{{ showSecret ? apiCredential.apiSecret : '••••••••••••••••••••••••••••••••' }}</code>
                  <button
                    type="button"
                    class="btn-copy btn-toggle"
                    @click="showSecret = !showSecret"
                    :title="showSecret ? '隱藏 Secret' : '顯示 Secret'"
                  >
                    <i :class="showSecret ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                  <button
                    v-if="showSecret"
                    type="button"
                    class="btn-copy"
                    @click="copyToClipboard(apiCredential.apiSecret, 'API Secret')"
                    title="複製 API Secret"
                  >
                    <i class="ri-file-copy-line"></i>
                  </button>
                </div>
              </div>
              <div class="credential-field">
                <label>狀態：</label>
                <span :class="['status-badge', `status-${apiCredential.status}`]">
                  {{ apiCredential.status === 'active' ? '啟用中' : '已撤銷' }}
                </span>
              </div>
              <div class="credential-field" v-if="apiCredential.name">
                <label>憑證名稱：</label>
                <span>{{ apiCredential.name }}</span>
              </div>
              <div class="credential-field">
                <label>建立時間：</label>
                <span>{{ formatDate(apiCredential.createdAt) }}</span>
              </div>
              <div class="credential-field" v-if="apiCredential.lastSecretRotatedAt">
                <label>最後輪換時間：</label>
                <span>{{ formatDate(apiCredential.lastSecretRotatedAt) }}</span>
              </div>
              
              <div class="credential-actions">
                <button
                  v-if="apiCredential.status === 'active'"
                  type="button"
                  class="btn btn-warning btn-sm"
                  @click="handleRotateSecret"
                  :disabled="loadingRotate"
                >
                  <i class="ri-refresh-line" :class="{ 'spinning': loadingRotate }"></i>
                  {{ loadingRotate ? '輪換中...' : '輪換 Secret' }}
                </button>
                <button
                  v-if="apiCredential.status === 'active'"
                  type="button"
                  class="btn btn-danger btn-sm"
                  @click="handleRevokeCredential"
                  :disabled="loadingRevoke"
                >
                  <i class="ri-close-circle-line"></i>
                  {{ loadingRevoke ? '撤銷中...' : '撤銷憑證' }}
                </button>
              </div>
            </div>
            <div v-else class="empty-state">
              <i class="ri-key-2-line"></i>
              <p>尚未建立 API 憑證</p>
              <button
                type="button"
                class="btn btn-primary"
                @click="handleCreateCredential"
                :disabled="loadingCreate"
              >
                <i class="ri-add-line"></i>
                {{ loadingCreate ? '建立中...' : '建立 API 憑證' }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="isEdit && editTab === 'sms'" class="form-section">
          <h3 class="section-title">簡訊點數</h3>
          <p class="section-description">全平台共用 Every8d 帳號。這裡記錄這個客戶購買與使用的點數，1 點等於 Every8d 的 1 點。</p>
          <div v-if="smsSummary" class="sms-point-board">
            <div class="sms-point-card is-balance" :class="{ 'is-negative': Number(smsSummary.balance) < 0 }">
              <span class="sms-point-label">目前餘額</span>
              <strong class="sms-point-value">{{ formatSmsPoints(smsSummary.balance) }}</strong>
            </div>
            <div class="sms-point-card is-threshold">
              <span class="sms-point-label">低餘額門檻</span>
              <strong class="sms-point-value">{{ formatSmsPoints(smsSummary.threshold) }}</strong>
              <span class="sms-point-hint">由客戶在系統參數自行設定</span>
            </div>
            <div
              v-if="isSystemAccount"
              class="sms-point-card is-dispatch"
              :class="{ 'is-empty': smsSummary.dispatchable != null && Number(smsSummary.dispatchable) <= 0 }"
            >
              <span class="sms-point-label">剩餘可派的最大點數</span>
              <strong class="sms-point-value">{{ smsSummary.dispatchable == null ? '—' : formatSmsPoints(smsSummary.dispatchable) }}</strong>
            </div>
          </div>
          <div v-if="isSystemAccount" class="form-row sms-dispatch-row">
            <div class="form-group">
              <label class="required">點數</label>
              <input v-model.number="smsForm.points" type="number" step="1" class="form-control" placeholder="正數為購買，負數為調整" />
            </div>
            <div class="form-group">
              <label class="required">金額（台幣）</label>
              <input v-model.number="smsForm.amountTwd" type="number" min="0" step="1" required class="form-control" />
            </div>
            <div class="form-group">
              <label>備註</label>
              <input v-model="smsForm.note" type="text" class="form-control" maxlength="500" />
            </div>
            <div class="form-group sms-dispatch-action">
              <button type="button" class="btn btn-primary sms-dispatch-btn" :disabled="smsSaving" @click="submitSmsPoints">
                {{ smsSaving ? '派送中...' : '派送簡訊點數' }}
              </button>
            </div>
          </div>
          <p v-else class="section-description">平台帳號可以查看餘額與歷史，不能派送或調整點數。</p>
          <div v-if="smsLoading">載入中...</div>
          <div v-else class="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>時間</th>
                  <th>類型</th>
                  <th>點數</th>
                  <th>餘額</th>
                  <th>金額</th>
                  <th>備註</th>
                  <th>操作者</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!smsEntries.length">
                  <td colspan="7">尚無紀錄</td>
                </tr>
                <tr v-for="row in smsEntries" :key="row.id">
                  <td class="sms-time">{{ formatSmsDateTime(row.createdAt) }}</td>
                  <td>{{ smsEntryLabel(row.entryType) }}</td>
                  <td :class="smsDeltaClass(row.pointsDelta)">{{ formatSmsPoints(row.pointsDelta) }}</td>
                  <td :class="{ 'sms-delta-minus': Number(row.balanceAfter) < 0 }">{{ formatSmsPoints(row.balanceAfter) }}</td>
                  <td>{{ row.amountTwd == null ? '' : row.amountTwd }}</td>
                  <td>{{ row.note }}</td>
                  <td>{{ row.createdByName }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="smsPagination.total > smsPagination.pageSize" class="form-group">
            <button type="button" class="btn btn-secondary" :disabled="smsPagination.page <= 1" @click="changeSmsPage(smsPagination.page - 1)">上一頁</button>
            <span>{{ smsPagination.page }} / {{ smsPageCount }}</span>
            <button type="button" class="btn btn-secondary" :disabled="smsPagination.page >= smsPageCount" @click="changeSmsPage(smsPagination.page + 1)">下一頁</button>
          </div>
        </div>

      </form>
    </div>

    <!-- API Secret 顯示 Modal -->
    <div v-if="showSecretModal" class="modal-overlay" @click="closeSecretModal">
      <div class="modal-content secret-modal" @click.stop>
        <div class="modal-header">
          <h3>
            <i class="ri-key-2-line"></i>
            {{ secretModalTitle }}
          </h3>
          <button class="btn-close" @click="closeSecretModal">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="alert alert-warning">
            <i class="ri-alert-line"></i>
            <strong>重要提醒：</strong>請立即複製並妥善保存 API Secret。關閉後也可在頁面上點擊眼睛圖標查看。
          </div>
          
          <div class="secret-display">
            <div class="secret-field">
              <label>API Key：</label>
              <div class="secret-value">
                <code>{{ secretData.apiKey }}</code>
                <button
                  type="button"
                  class="btn-copy"
                  @click="copyToClipboard(secretData.apiKey, 'API Key')"
                >
                  <i class="ri-file-copy-line"></i>
                  複製
                </button>
              </div>
            </div>
            
            <div class="secret-field">
              <label>API Secret：</label>
              <div class="secret-value">
                <code class="secret-code">{{ secretData.apiSecret }}</code>
                <button
                  type="button"
                  class="btn-copy btn-copy-primary"
                  @click="copyToClipboard(secretData.apiSecret, 'API Secret')"
                >
                  <i class="ri-file-copy-line"></i>
                  複製
                </button>
              </div>
            </div>
          </div>

          <div class="alert alert-info">
            <i class="ri-information-line"></i>
            <div>
              <strong>使用說明：</strong>
              <ol>
                <li>將 API Key 和 Secret 提供給合作平台（透過安全管道）</li>
                <li>合作平台使用這組憑證呼叫換 Token API：<code>POST /api/v1/auth/client/token-by-api-key</code></li>
                <li>取得 Token 後，合作平台即可存取該客戶的所有資料與 API</li>
              </ol>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-primary" @click="closeSecretModal">
            <i class="ri-check-line"></i>
            我已複製並保存
          </button>
        </div>
      </div>
    </div>

    <!-- ChatDesk 設定座席 Modal -->
    <div v-if="showSeatsModal" class="modal-overlay" @click.self="showSeatsModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>設定 ChatDesk 座席</h3>
          <button type="button" class="btn-close" @click="showSeatsModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="form-hint">
            內含 3 席；加購 NT$250／席／月。加席立刻生效（人工可按比例收款）；減席下期生效、本期不退。
          </p>
          <div class="form-group">
            <label for="createExtraSeats" class="required">加購座席數</label>
            <input
              id="createExtraSeats"
              v-model.number="seatsForm.extraSeats"
              type="number"
              class="form-control"
              min="0"
              step="1"
            />
            <p class="form-hint">
              目前加購 {{ seatsCurrentExtra }} →
              變更為 {{ Number(seatsForm.extraSeats) || 0 }}；
              總座席 {{ 3 + (Number(seatsForm.extraSeats) || 0) }}
            </p>
          </div>
          <div
            v-if="seatsChangeIsIncrease && form.billingMethod !== 'self_subscription'"
            class="form-group"
          >
            <label class="check-row">
              <input type="checkbox" v-model="seatsForm.recordPayment" />
              同時記錄人工收款（不延長訂閱）
            </label>
            <input
              v-if="seatsForm.recordPayment"
              v-model="seatsForm.paymentDate"
              type="date"
              class="form-control"
              style="margin-top: 8px; max-width: 220px"
            />
          </div>
          <p v-else-if="seatsChangeIsIncrease" class="form-hint" style="color:#b45309">
            自助訂閱客戶加席不會自動調整綠界金額。
          </p>
          <p v-else-if="seatsChangeIsDecrease" class="form-hint" style="color:#b45309">
            減席將於訂閱到期日後生效，本期不退款。
          </p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showSeatsModal = false">取消</button>
          <button
            type="button"
            class="btn btn-primary"
            @click="handleUpdateSeats"
            :disabled="seatsSubmitting"
          >
            {{ seatsSubmitting ? '儲存中...' : '儲存' }}
          </button>
        </div>
      </div>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { clientService } from '@/api/modules/clients.js';
import { platformManagementService } from '@/api/modules/platforms.js';
import { planService } from '@/api/modules/plans.js';
import { clientOptionalModuleService } from '@/api/modules/clientOptionalModules.js';
import { showSuccess, showError, showConfirm } from '@/utils/swal.js';
import { smsEntryLabel, formatSmsPoints } from '@/utils/smsPointLabels.js';
import authService from '@/helpers/authservice/authService.js';

export default {
  name: 'CreateClient',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const loadingPlatforms = ref(false);
    const loadingPlans = ref(false);
    const loadingOptionalModules = ref(false);
    const loadingGenerate = ref(false);
    const cancellingModuleId = ref(null);
    const errorMessage = ref('');
    const errors = reactive({});
    
    // API 憑證相關狀態
    const apiCredential = ref(null);
    const loadingCredential = ref(false);
    const loadingCreate = ref(false);
    const loadingRotate = ref(false);
    const loadingRevoke = ref(false);
    const showSecretModal = ref(false);
    const secretModalTitle = ref('');
    const secretData = ref({ apiKey: '', apiSecret: '' });
    const showSecret = ref(false); // 控制是否顯示 Secret
    const platforms = ref([]);
    const plans = ref([]);
    const optionalModules = ref([]);
    const showSeatsModal = ref(false);
    const seatsSubmitting = ref(false);
    const seatsTargetModule = ref(null);
    const seatsForm = reactive({
      extraSeats: 0,
      recordPayment: true,
      paymentDate: new Date().toISOString().slice(0, 10)
    });

    const seatsCurrentExtra = computed(() => seatsTargetModule.value?.seats?.extraSeats ?? 0);
    const seatsChangeIsIncrease = computed(() => {
      const next = Number(seatsForm.extraSeats);
      return Number.isFinite(next) && next > seatsCurrentExtra.value;
    });
    const seatsChangeIsDecrease = computed(() => {
      const next = Number(seatsForm.extraSeats);
      return Number.isFinite(next) && next < seatsCurrentExtra.value;
    });

    const isEdit = computed(() => {
      return !!route.params.id;
    });

    const isSystemAccount = computed(() => {
      return authService.getAccountType() === 'system';
    });

    const isPlatformAccount = computed(() => {
      return authService.getAccountType() === 'platform';
    });

    const selectedPlan = computed(() => {
      if (!form.planId || !plans.value.length) return null;
      return plans.value.find(p => p.id === form.planId) || null;
    });

    const planLimitDisplay = computed(() => {
      const p = selectedPlan.value;
      if (!p) {
        return { maxStores: '—', maxMembers: '—', maxLineAccounts: '—', maxLineFriends: '—' };
      }
      return {
        maxStores: p.maxStores != null ? String(p.maxStores) : '不限制',
        maxMembers: p.maxMembers != null ? String(p.maxMembers) : '不限制',
        maxLineAccounts: p.maxLineConfigs != null ? String(p.maxLineConfigs) : '不限制',
        maxLineFriends: '—',
      };
    });

    const form = reactive({
      platformId: '',
      clientCode: '',
      clientName: '',
      planId: '',
      industry: '',
      status: 'active',
      maxStores: null,
      maxMembers: null,
      maxLineAccounts: null,
      maxLineFriends: null,
      billingMethod: 'manual_billing',
      billingStatus: 'active',
      memberSystemType: 'LOCKLINE',
      subscriptionNeverExpires: false,
    });

    // 獲取平台列表（僅系統帳號需要）
    const fetchPlatforms = async () => {
      if (!isSystemAccount.value) {
        return;
      }
      
      loadingPlatforms.value = true;
      try {
        const response = await platformManagementService.getPlatforms({ page: 1, pageSize: 100 });
        
        if (response && response.success && response.data && response.data.items) {
          platforms.value = response.data.items.map(platform => ({
            id: platform.id,
            name: platform.platformName,
            code: platform.platformCode,
          }));
        }
      } catch (err) {
        console.error('獲取平台列表失敗:', err);
        await showError('獲取平台列表失敗', '請稍後再試');
      } finally {
        loadingPlatforms.value = false;
      }
    };

    // 獲取當前平台資訊（平台帳號使用）
    const currentPlatformName = computed(() => {
      if (isPlatformAccount.value && platforms.value.length > 0) {
        return platforms.value[0].name;
      }
      return '當前平台';
    });

    // 初始化平台帳號的平台 ID
    const initPlatformAccount = async () => {
      if (!isPlatformAccount.value) {
        return;
      }

      // 平台帳號自動設置 platformId
      const user = authService.getCurrentUser();
      const platformId = user?.platformId;
      if (platformId) {
        form.platformId = platformId;
        
        // 獲取平台名稱用於顯示
        try {
          const response = await platformManagementService.getPlatforms({ page: 1, pageSize: 100 });
          if (response && response.success && response.data && response.data.items) {
            const currentPlatform = response.data.items.find(p => p.id === platformId);
            if (currentPlatform) {
              platforms.value = [{
                id: currentPlatform.id,
                name: currentPlatform.platformName,
                code: currentPlatform.platformCode,
              }];
            }
          }
        } catch (err) {
          console.error('獲取平台資訊失敗:', err);
        }
      }
    };

    // 獲取方案列表
    // ⚠️ 重要：只載入啟用的方案
    // 停用方案的影響：
    // 1. 不會出現在此下拉選單中
    // 2. 無法在客戶建立/編輯時被指定
    // 3. 不會出現在公開 API 的方案列表中
    // 4. 但已使用該方案的現有客戶不受影響，仍可正常使用
    const fetchPlans = async () => {
      loadingPlans.value = true;
      try {
        // 只載入啟用的方案，避免選擇停用的方案
        const response = await planService.getPlans({ 
          page: 1, 
          pageSize: 100,
          status: 'active'  // 只載入啟用的方案
        });
        
        if (response && response.success && response.data && response.data.items) {
          plans.value = response.data.items.map(plan => ({
            id: plan.id,
            planName: plan.planName,
            planCode: plan.planCode,
            maxStores: plan.maxStores ?? null,
            maxMembers: plan.maxMembers ?? null,
            maxLineConfigs: plan.maxLineConfigs ?? null,
          }));
        }
      } catch (err) {
        console.error('獲取方案列表失敗:', err);
        // 不需要顯示錯誤，方案是可選的
      } finally {
        loadingPlans.value = false;
      }
    };

    const validateForm = () => {
      // 清除之前的錯誤
      Object.keys(errors).forEach(key => delete errors[key]);
      errorMessage.value = '';

      let isValid = true;

      // 驗證平台
      if (!isEdit.value && !form.platformId) {
        errors.platformId = '請選擇平台';
        isValid = false;
      }

      // 驗證客戶代碼
      if (!isEdit.value && (!form.clientCode || form.clientCode.trim().length < 2)) {
        errors.clientCode = '客戶代碼至少需要 2 個字元';
        isValid = false;
      }

      // 驗證客戶名稱
      if (!form.clientName || form.clientName.trim().length === 0) {
        errors.clientName = '請輸入客戶名稱';
        isValid = false;
      }

      // 驗證門市數上限（如果提供了值，必須大於 0）
      if (form.maxStores !== undefined && form.maxStores !== null && form.maxStores !== '' && form.maxStores < 1) {
        errors.maxStores = '門市數上限必須大於 0';
        isValid = false;
      }

      // 驗證會員數上限（如果提供了值，必須大於 0）
      if (form.maxMembers !== undefined && form.maxMembers !== null && form.maxMembers !== '' && form.maxMembers < 1) {
        errors.maxMembers = '會員數上限必須大於 0';
        isValid = false;
      }

      if (form.maxLineAccounts !== undefined && form.maxLineAccounts !== null && form.maxLineAccounts !== '' && form.maxLineAccounts < 1) {
        errors.maxLineAccounts = 'LINE 官方帳號上限必須大於 0';
        isValid = false;
      }
      if (form.maxLineFriends !== undefined && form.maxLineFriends !== null && form.maxLineFriends !== '' && form.maxLineFriends < 1) {
        errors.maxLineFriends = '好友數上限必須大於 0';
        isValid = false;
      }

      return isValid;
    };

    const fetchClient = async () => {
      if (!isEdit.value) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const response = await clientService.getClient(route.params.id);

        if (response && response.success && response.data && response.data.clientData) {
          const client = response.data.clientData;
          form.platformId = client.platformId;
          form.clientCode = client.clientCode;
          form.clientName = client.clientName;
          form.planId = client.planId || '';
          form.industry = client.industry || '';
          form.status = client.status;
          form.maxStores = client.maxStores ?? null;
          form.maxMembers = client.maxMembers ?? null;
          form.maxLineAccounts = client.maxLineAccounts ?? null;
          form.maxLineFriends = client.maxLineFriends ?? null;
          form.billingMethod = client.billingMethod || 'manual_billing';
          form.billingStatus = client.billingStatus || 'active';
          form.memberSystemType = client.memberSystemType || 'LOCKLINE';
          form.subscriptionNeverExpires = client.subscriptionNeverExpires === true;
        } else {
          await showError('獲取失敗', response && response.message || '獲取客戶資料失敗');
          router.push({ name: 'ClientList' });
        }
      } catch (error) {
        console.error('獲取客戶資料失敗:', error);
        let errorMessage = '獲取客戶資料失敗，請稍後再試';
        if (error.code === 'NOT_FOUND') {
          errorMessage = '客戶不存在或已被刪除';
        } else if (error.response?.status === 404) {
          errorMessage = '客戶不存在或路由錯誤';
        } else if (error.response?.status === 403) {
          errorMessage = '您沒有權限查看此客戶';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        await showError('獲取失敗', errorMessage);
        router.push({ name: 'ClientList' });
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const clientData = {
          clientCode: form.clientCode.trim(),
          clientName: form.clientName.trim(),
          status: form.status,
          billingMethod: form.billingMethod,
          billingStatus: form.billingStatus,
          memberSystemType: form.memberSystemType,
        };

        if (isSystemAccount.value) {
          clientData.subscriptionNeverExpires = form.subscriptionNeverExpires === true;
        }

        // 只有當有值時才加入
        if (form.industry) {
          clientData.industry = form.industry.trim();
        }
        if (form.planId) {
          clientData.planId = form.planId;
        }
        // 數字欄位：如果是空字串或 null/undefined，則傳 null（表示清空），否則傳數字
        clientData.maxStores = (form.maxStores === '' || form.maxStores === null || form.maxStores === undefined) 
          ? null 
          : Number(form.maxStores);
        clientData.maxMembers = (form.maxMembers === '' || form.maxMembers === null || form.maxMembers === undefined) 
          ? null 
          : Number(form.maxMembers);
        clientData.maxLineAccounts = (form.maxLineAccounts === '' || form.maxLineAccounts === null || form.maxLineAccounts === undefined) 
          ? null 
          : Number(form.maxLineAccounts);
        clientData.maxLineFriends = (form.maxLineFriends === '' || form.maxLineFriends === null || form.maxLineFriends === undefined) 
          ? null 
          : Number(form.maxLineFriends);

        let response;
        if (isEdit.value) {
          response = await clientService.updateClient(route.params.id, clientData);
        } else {
          clientData.platformId = form.platformId;
          response = await clientService.createClient(clientData);
        }

        if (response && response.success) {
          await showSuccess(
            isEdit.value ? '更新成功' : '新增成功',
            `客戶「${clientData.clientName}」已成功${isEdit.value ? '更新' : '建立'}`
          );
          router.push({ name: 'ClientList' });
        } else {
          let errorTitle = isEdit.value ? '更新失敗' : '新增失敗';
          let errorText = response?.message || `${isEdit.value ? '更新' : '新增'}客戶失敗`;

          if (response?.error?.code === 'CLIENT_CODE_EXISTS') {
            errors.clientCode = '客戶代碼已存在';
            errorText = '客戶代碼已存在';
          }
          await showError(errorTitle, errorText);
        }
      } catch (error) {
        console.error(`${isEdit.value ? '更新' : '新增'}客戶失敗:`, error);
        let errorMessage = `${isEdit.value ? '更新' : '新增'}客戶失敗，請稍後再試`;
        if (error.code === 'CLIENT_CODE_EXISTS') {
          errorMessage = '客戶代碼已存在，請使用其他代碼';
        } else if (error.code === 'NOT_FOUND') {
          errorMessage = '客戶不存在或已被刪除';
        } else if (error.code === 'FORBIDDEN') {
          errorMessage = '您沒有權限執行此操作';
        } else if (error.response?.status === 401) {
          errorMessage = '未授權，請重新登入';
        } else if (error.response?.status === 403) {
          errorMessage = '您沒有權限執行此操作';
        } else if (error.response?.status === 400) {
          errorMessage = error.response?.data?.message || '輸入資料驗證失敗，請檢查輸入內容';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        await showError(
          isEdit.value ? '更新失敗' : '新增失敗',
          errorMessage
        );
      } finally {
        loading.value = false;
      }
    };

    const handleCancel = () => {
      router.push({ name: 'ClientList' });
    };

    // 生成客戶代碼
    const handleGenerateCode = async () => {
      // 確認已選擇平台
      if (!form.platformId) {
        await showError('生成失敗', '請先選擇平台');
        return;
      }

      loadingGenerate.value = true;
      try {
        const response = await clientService.generateClientCode(form.platformId);
        
        if (response && response.success && response.data && response.data.clientCode) {
          form.clientCode = response.data.clientCode;
          await showSuccess('生成成功', `已生成客戶代碼：${response.data.clientCode}`);
        } else {
          await showError('生成失敗', response?.message || '生成客戶代碼失敗');
        }
      } catch (error) {
        console.error('生成客戶代碼失敗:', error);
        let errorMessage = '生成客戶代碼失敗，請稍後再試';
        
        if (error.code === 'CODE_LIMIT_EXCEEDED') {
          errorMessage = '當年度客戶代碼已達上限，請聯繫系統管理員';
        } else if (error.code === 'CODE_CONFLICT') {
          errorMessage = '生成的客戶代碼已存在，請重試';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        await showError('生成失敗', errorMessage);
      } finally {
        loadingGenerate.value = false;
      }
    };

    const goToOptionalModules = () => {
      router.push({ name: 'ClientOptionalModules', params: { id: route.params.id } });
    };

    const openSeatsModal = (module) => {
      seatsTargetModule.value = module;
      seatsForm.extraSeats = module.seats?.extraSeats ?? 0;
      seatsForm.recordPayment = form.billingMethod !== 'self_subscription';
      seatsForm.paymentDate = new Date().toISOString().slice(0, 10);
      showSeatsModal.value = true;
    };

    const handleUpdateSeats = async () => {
      const seats = Number(seatsForm.extraSeats);
      if (!Number.isFinite(seats) || seats < 0 || !Number.isInteger(seats)) {
        await showError('輸入錯誤', '請輸入 0 或正整數');
        return;
      }
      if (!seatsTargetModule.value) return;

      const options = {};
      if (seats > seatsCurrentExtra.value
        && seatsForm.recordPayment
        && form.billingMethod !== 'self_subscription') {
        options.recordPayment = true;
        options.paymentDate = seatsForm.paymentDate;
      }

      seatsSubmitting.value = true;
      try {
        const response = await clientOptionalModuleService.updateChatDeskSeats(
          route.params.id,
          seatsTargetModule.value.module_id,
          seats,
          options
        );
        const result = response?.data?.module || {};
        let message = response?.message || `已設定加購 ${seats} 席`;
        if (result.decreaseSchedule?.message) {
          message = result.decreaseSchedule.message;
        } else if (result.proratedInfo) {
          const p = result.proratedInfo;
          message = `已加席至 ${seats}（總座席 ${3 + seats}）\n` +
            `本期應收：NT$ ${formatCurrency(p.proratedAmount)}` +
            (result.paymentRecord ? '（已記錄收款）' : '');
        }
        if (result.ecpayBillingNote) {
          message += `\n\n注意：${result.ecpayBillingNote}`;
        }
        await showSuccess('儲存成功', message);
        showSeatsModal.value = false;
        await fetchOptionalModules();
      } catch (error) {
        await showError('儲存失敗', error?.message || '更新座席失敗');
      } finally {
        seatsSubmitting.value = false;
      }
    };

    // 載入選購模組列表
    const fetchOptionalModules = async () => {
      if (!isEdit.value) return;
      
      loadingOptionalModules.value = true;
      try {
        const response = await clientOptionalModuleService.getClientOptionalModules(route.params.id);
        if (response?.success && response?.data) {
          optionalModules.value = response.data.modules || [];
        }
      } catch (error) {
        console.error('載入選購模組失敗:', error);
        // 不顯示錯誤，避免影響頁面載入
        optionalModules.value = [];
      } finally {
        loadingOptionalModules.value = false;
      }
    };

    // 格式化貨幣
    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return '0.00';
      return parseFloat(amount).toLocaleString('zh-TW', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    const formatSmsDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '-';
      const pad = (value) => String(value).padStart(2, '0');
      return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    // 取消選購模組
    const handleCancelModule = async (module) => {
      const confirmed = await showConfirm(
        '確認取消選購模組',
        `確定要取消「${module.module_name}」模組嗎？取消後將在下個計費週期結束時失效。`,
        '確認取消',
        '取消'
      );

      if (!confirmed) return;

      cancellingModuleId.value = module.module_id;
      try {
        const response = await clientOptionalModuleService.cancelOptionalModule(
          route.params.id,
          module.module_id
        );

        if (response?.success) {
          await showSuccess('取消成功', '選購模組已取消，將在下個計費週期結束時失效');
          await fetchOptionalModules(); // 重新載入模組列表
        } else {
          await showError('取消失敗', response?.message || '取消選購模組失敗');
        }
      } catch (error) {
        const errorMessage = error?.message || '取消選購模組失敗，請稍後再試';
        await showError('取消失敗', errorMessage);
      } finally {
        cancellingModuleId.value = null;
      }
    };

    // 載入 API 憑證
    const fetchApiCredential = async () => {
      if (!isEdit.value || !isSystemAccount.value) return;
      
      loadingCredential.value = true;
      try {
        const response = await clientService.getApiCredential(route.params.id);
        if (response?.success && response?.data) {
          apiCredential.value = response.data;
        } else {
          apiCredential.value = null;
        }
      } catch (error) {
        // 如果是 404（憑證不存在），不視為錯誤，靜默處理
        const status = error?.response?.status || error?.status;
        if (status === 404 || error.code === 'NOT_FOUND' || error.message?.includes('尚未建立')) {
          apiCredential.value = null;
        } else {
          console.error('載入 API 憑證失敗:', error);
          await showError('載入失敗', error?.message || '載入 API 憑證失敗');
        }
      } finally {
        loadingCredential.value = false;
      }
    };

    // 建立 API 憑證
    const handleCreateCredential = async () => {
      loadingCreate.value = true;
      try {
        const response = await clientService.createApiCredential(route.params.id, {
          name: `${form.clientName} 的 API 憑證`
        });

        if (response?.success && response?.data) {
          // 保留完整的憑證資料（包含 apiSecret）
          apiCredential.value = response.data;
          
          // 顯示 Secret Modal（首次建立時的提醒）
          secretModalTitle.value = 'API 憑證已建立';
          secretData.value = {
            apiKey: response.data.apiKey,
            apiSecret: response.data.apiSecret
          };
          showSecretModal.value = true;
          
          await showSuccess('建立成功', 'API 憑證已建立，請立即複製 Secret');
        } else {
          await showError('建立失敗', response?.message || '建立 API 憑證失敗');
        }
      } catch (error) {
        console.error('建立 API 憑證失敗:', error);
        await showError('建立失敗', error?.message || '建立 API 憑證失敗');
      } finally {
        loadingCreate.value = false;
      }
    };

    // 輪換 API Secret
    const handleRotateSecret = async () => {
      const confirmed = await showConfirm(
        '確認輪換 Secret',
        '輪換後，舊的 Secret 將立即失效，合作平台需要更新為新的 Secret。確定要繼續嗎？',
        '確認輪換',
        '取消'
      );

      if (!confirmed) return;

      loadingRotate.value = true;
      try {
        const response = await clientService.rotateApiSecret(route.params.id);

        if (response?.success && response?.data) {
          // 更新憑證資料，包含新的 Secret
          apiCredential.value = {
            ...apiCredential.value,
            apiSecret: response.data.apiSecret,
            lastSecretRotatedAt: response.data.lastSecretRotatedAt
          };
          
          // 顯示新 Secret（首次輪換時的提醒）
          secretModalTitle.value = 'Secret 已輪換';
          secretData.value = {
            apiKey: apiCredential.value.apiKey,
            apiSecret: response.data.apiSecret
          };
          showSecretModal.value = true;
          
          await showSuccess('輪換成功', '新的 Secret 已生成，請立即複製');
        } else {
          await showError('輪換失敗', response?.message || '輪換 Secret 失敗');
        }
      } catch (error) {
        console.error('輪換 Secret 失敗:', error);
        await showError('輪換失敗', error?.message || '輪換 Secret 失敗');
      } finally {
        loadingRotate.value = false;
      }
    };

    // 撤銷 API 憑證
    const handleRevokeCredential = async () => {
      const confirmed = await showConfirm(
        '確認撤銷憑證',
        '撤銷後，合作平台將無法再使用此憑證存取 API，且無法恢復。確定要撤銷嗎？',
        '確認撤銷',
        '取消',
        'warning'
      );

      if (!confirmed) return;

      loadingRevoke.value = true;
      try {
        const response = await clientService.revokeApiCredential(route.params.id);

        if (response?.success) {
          apiCredential.value = null;
          await showSuccess('撤銷成功', 'API 憑證已撤銷');
        } else {
          await showError('撤銷失敗', response?.message || '撤銷憑證失敗');
        }
      } catch (error) {
        console.error('撤銷憑證失敗:', error);
        await showError('撤銷失敗', error?.message || '撤銷憑證失敗');
      } finally {
        loadingRevoke.value = false;
      }
    };

    // 關閉 Secret Modal
    const closeSecretModal = () => {
      showSecretModal.value = false;
      secretModalTitle.value = '';
      secretData.value = { apiKey: '', apiSecret: '' };
    };

    // 複製到剪貼簿
    const copyToClipboard = async (text, label) => {
      try {
        await navigator.clipboard.writeText(text);
        await showSuccess('複製成功', `${label} 已複製到剪貼簿`);
      } catch (error) {
        console.error('複製失敗:', error);
        await showError('複製失敗', '無法複製到剪貼簿，請手動複製');
      }
    };

    // 編輯模式 Tab
    const editTab = ref('basic');
    const smsLoading = ref(false);
    const smsSaving = ref(false);
    const smsSummary = ref(null);
    const smsEntries = ref([]);
    const smsPagination = ref({ page: 1, pageSize: 20, total: 0 });
    const smsForm = reactive({ points: null, amountTwd: null, note: '' });
    const smsPageCount = computed(() => Math.max(1, Math.ceil((smsPagination.value.total || 0) / smsPagination.value.pageSize)));

    const fetchSmsPoints = async (page = 1) => {
      if (!isEdit.value) return;
      smsLoading.value = true;
      try {
        const response = await clientService.getSmsPoints(route.params.id, { page, pageSize: 20 });
        const data = response?.data || {};
        smsSummary.value = data;
        smsEntries.value = data.items || [];
        smsPagination.value = data.pagination || { page, pageSize: 20, total: 0 };
      } catch (err) {
        await showError('載入失敗', err?.response?.data?.message || '無法載入簡訊點數');
      } finally {
        smsLoading.value = false;
      }
    };

    const openSmsTab = () => {
      editTab.value = 'sms';
      fetchSmsPoints(smsPagination.value.page || 1);
    };

    const changeSmsPage = (page) => fetchSmsPoints(page);

    const smsDeltaClass = (value) => {
      const points = Number(value);
      if (!Number.isFinite(points) || points === 0) return '';
      return points > 0 ? 'sms-delta-plus' : 'sms-delta-minus';
    };

    const submitSmsPoints = async () => {
      const points = Number(smsForm.points);
      if (!Number.isFinite(points) || points === 0) {
        await showError('無法派送', '點數不可為 0');
        return;
      }
      const rawAmount = smsForm.amountTwd;
      const amountTwd = Number(rawAmount);
      if (rawAmount === null || rawAmount === '' || !Number.isFinite(amountTwd) || amountTwd < 0) {
        await showError('無法派送', '請填寫金額（台幣）');
        return;
      }
      smsSaving.value = true;
      try {
        const payload = { points, amountTwd, note: smsForm.note || null };
        await clientService.addSmsPoints(route.params.id, payload);
        smsForm.points = null;
        smsForm.amountTwd = null;
        smsForm.note = '';
        await showSuccess('已派送', '簡訊點數已寫入歷史');
        await fetchSmsPoints(1);
      } catch (err) {
        await showError('派送失敗', err?.response?.data?.message || err?.response?.data?.error?.message || '請稍後再試');
      } finally {
        smsSaving.value = false;
      }
    };

    // LINE 設定（系統管理員控制）
    const loadingLineSettings = ref(false);
    const lineSettingsSaving = ref(false);
    const lineSettings = ref({ crossOaBindingMode: 'phone_verification' });

    const fetchLineSettings = async () => {
      if (!isEdit.value) return;
      loadingLineSettings.value = true;
      try {
        const response = await clientService.getLineSettings(route.params.id);
        if (response?.success && response?.data) {
          lineSettings.value = {
            crossOaBindingMode: 'phone_verification',
            ...response.data
          };
        }
      } catch (err) {
        console.error('獲取 LINE 設定失敗:', err);
      } finally {
        loadingLineSettings.value = false;
      }
    };

    const handleSaveLineSettings = async () => {
      lineSettingsSaving.value = true;
      try {
        await clientService.updateLineSettings(route.params.id, {
          crossOaBindingMode: lineSettings.value.crossOaBindingMode
        });
        await showSuccess('儲存成功', 'LINE 設定已更新');
      } catch (err) {
        await showError('儲存失敗', err?.response?.data?.message ?? '請稍後再試');
      } finally {
        lineSettingsSaving.value = false;
      }
    };

    onMounted(async () => {
      await initPlatformAccount();
      await fetchPlatforms();
      await fetchPlans();
      if (isEdit.value) {
        await fetchClient();
        await fetchOptionalModules();
        await fetchApiCredential();
        await fetchLineSettings();
      }
    });

    return {
      form,
      errors,
      errorMessage,
      loading,
      loadingPlatforms,
      loadingPlans,
      loadingOptionalModules,
      loadingGenerate,
      cancellingModuleId,
      platforms,
      plans,
      optionalModules,
      selectedPlan,
      planLimitDisplay,
      isEdit,
      editTab,
      smsLoading,
      smsSaving,
      smsSummary,
      smsEntries,
      smsPagination,
      smsForm,
      smsPageCount,
      openSmsTab,
      changeSmsPage,
      submitSmsPoints,
      smsEntryLabel,
      smsDeltaClass,
      formatSmsPoints,
      formatSmsDateTime,
      isSystemAccount,
      isPlatformAccount,
      currentPlatformName,
      handleSubmit,
      handleCancel,
      handleGenerateCode,
      goToOptionalModules,
      handleCancelModule,
      showSeatsModal,
      seatsSubmitting,
      seatsForm,
      seatsCurrentExtra,
      seatsChangeIsIncrease,
      seatsChangeIsDecrease,
      openSeatsModal,
      handleUpdateSeats,
      formatCurrency,
      formatDate,
      // API 憑證相關
      apiCredential,
      loadingCredential,
      loadingCreate,
      loadingRotate,
      loadingRevoke,
      showSecretModal,
      secretModalTitle,
      secretData,
      showSecret,
      handleCreateCredential,
      handleRotateSecret,
      handleRevokeCredential,
      closeSecretModal,
      copyToClipboard,
      // LINE 設定相關
      loadingLineSettings,
      lineSettingsSaving,
      lineSettings,
      handleSaveLineSettings,
    };
  },
};
</script>

<style scoped>
.create-client-container {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.perpetual-check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  cursor: pointer;
}

.perpetual-check input {
  width: 16px;
  height: 16px;
}

/* 編輯模式 Tab 導航 */
.edit-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #e9ecef;
  margin: -24px -24px 24px -24px;
  padding: 0 24px;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
  overflow-x: auto;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 18px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;
}

.tab-btn i {
  font-size: 16px;
}

.tab-btn:hover {
  color: #495057;
}

.tab-btn.active {
  color: #0d6efd;
  border-bottom-color: #0d6efd;
  font-weight: 600;
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
}

.optional-modules-section {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.loading-state {
  text-align: center;
  padding: 24px;
  color: #999;
}

.loading-state .spin {
  animation: spin 1s linear infinite;
  display: inline-block;
  margin-right: 8px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.optional-modules-list {
  margin-bottom: 20px;
}

.modules-list-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.module-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s;
}

.module-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.module-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.module-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
  flex: 1;
}

.module-code {
  font-size: 12px;
  color: #666;
  background: #e0e0e0;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 8px;
}

.module-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}

.detail-label {
  color: #666;
}

.detail-value {
  color: #333;
  font-weight: 500;
}

.empty-state {
  text-align: center;
  padding: 32px;
  color: #999;
  margin-bottom: 20px;
}

.empty-state i {
  font-size: 48px;
  display: block;
  margin-bottom: 12px;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

.module-actions {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-warning {
  background: #ffc107;
  color: #333;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
}

.btn-warning:hover:not(:disabled) {
  background: #ffb300;
}

.btn-warning:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cancelled-badge {
  font-size: 12px;
  color: #999;
  font-style: italic;
}

.section-description {
  color: #666;
  font-size: 14px;
  margin-bottom: 16px;
  line-height: 1.6;
}

.sms-point-board {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0 0 20px;
}

.sms-point-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 108px;
  padding: 16px 18px;
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  background: #f7f9fc;
}

.sms-point-card.is-balance {
  background: #e8f1ff;
  border-color: #90b8f8;
}

.sms-point-card.is-balance .sms-point-label,
.sms-point-card.is-balance .sms-point-value {
  color: #1565c0;
}

.sms-point-card.is-balance.is-negative {
  background: #fdecea;
  border-color: #f5b7b1;
}

.sms-point-card.is-balance.is-negative .sms-point-label,
.sms-point-card.is-balance.is-negative .sms-point-value {
  color: #c62828;
}

.sms-point-card.is-threshold {
  background: #f3e8ff;
  border-color: #c4a3e8;
}

.sms-point-card.is-threshold .sms-point-label,
.sms-point-card.is-threshold .sms-point-value {
  color: #6a1b9a;
}

.sms-point-card.is-threshold .sms-point-hint {
  color: #8e5bb5;
}

.sms-point-label {
  font-size: 13px;
  font-weight: 600;
  color: #5f6b7a;
}

.sms-point-value {
  font-size: 32px;
  line-height: 1.1;
  font-weight: 700;
  color: #1f2933;
}

.sms-point-hint {
  margin-top: auto;
  font-size: 12px;
  line-height: 1.4;
  color: #6b7280;
}

.sms-point-card.is-dispatch {
  background: #e8f5e9;
  border-color: #81c784;
}

.sms-point-card.is-dispatch .sms-point-label {
  color: #2e7d32;
}

.sms-point-card.is-dispatch .sms-point-value {
  font-size: 36px;
  color: #1b5e20;
}

.sms-point-card.is-dispatch.is-empty {
  background: #fff4e5;
  border-color: #ffb74d;
}

.sms-point-card.is-dispatch.is-empty .sms-point-label,
.sms-point-card.is-dispatch.is-empty .sms-point-value {
  color: #e65100;
}

.form-row.sms-dispatch-row {
  grid-template-columns: minmax(180px, 1.2fr) minmax(140px, 1fr) minmax(180px, 1.6fr) auto;
  align-items: end;
}

.sms-dispatch-row .form-control,
.sms-dispatch-btn {
  height: 42px;
  box-sizing: border-box;
}

.sms-dispatch-btn {
  white-space: nowrap;
}

.sms-time {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.sms-delta-plus {
  color: #1b5e20;
  font-weight: 700;
}

.sms-delta-minus {
  color: #c62828;
  font-weight: 700;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.input-with-button {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.input-with-button .form-control {
  flex: 1;
}

.btn-generate {
  padding: 10px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-generate:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-generate:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-generate .spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.form-control {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.form-hint {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.form-hint i {
  font-size: 14px;
}

.plan-limit-box {
  margin-top: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #0d6efd;
  background: #e7f1ff;
  border: 1px solid #b6d4fe;
  border-radius: 6px;
  font-weight: 500;
}

.plan-limit-box strong {
  color: #0a58ca;
}

.alert {
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.alert-danger {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.line-binding-mode-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mode-option-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
}

.mode-option-label.active {
  border-color: #06c755;
  background-color: #f0fff4;
}

.mode-radio-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #ccc;
  flex-shrink: 0;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s;
}

.mode-radio-dot.checked {
  border-color: #06c755;
  background: #06c755;
  box-shadow: inset 0 0 0 3px white;
}

.mode-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mode-text strong {
  font-size: 14px;
  color: #333;
}

.mode-desc {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
}

.form-actions-inline {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  justify-content: flex-end;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* API 憑證管理區塊樣式 */
.api-credentials-section {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.section-description {
  margin-bottom: 20px;
  color: #6c757d;
  font-size: 14px;
  line-height: 1.6;
}

.credential-info {
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.credential-field {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
}

.credential-field:last-of-type {
  margin-bottom: 20px;
}

.credential-field label {
  min-width: 120px;
  font-weight: 500;
  color: #495057;
}

.credential-value {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.credential-value code {
  background: #f8f9fa;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  color: #495057;
  flex: 1;
  word-break: break-all;
}

.btn-copy {
  padding: 6px 12px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
}

.btn-copy:hover {
  background: #5a6268;
}

.btn-toggle {
  background: #17a2b8;
}

.btn-toggle:hover {
  background: #138496;
}

.btn-copy-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.btn-copy-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-active {
  background: #d4edda;
  color: #155724;
}

.status-revoked {
  background: #f8d7da;
  color: #721c24;
}

.credential-actions {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

.btn-warning {
  background: #ffc107;
  color: #000;
}

.btn-warning:hover:not(:disabled) {
  background: #e0a800;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #c82333;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Modal 樣式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.secret-modal {
  max-width: 700px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #f8f9fa;
  color: #495057;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.alert-warning {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.alert-warning i {
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.alert-info {
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
  padding: 12px 16px;
  border-radius: 6px;
  margin-top: 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.alert-info i {
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.alert-info ol {
  margin: 8px 0 0 0;
  padding-left: 20px;
}

.alert-info li {
  margin-bottom: 4px;
}

.alert-info code {
  background: rgba(0, 0, 0, 0.1);
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
}

.secret-display {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

.secret-field {
  margin-bottom: 16px;
}

.secret-field:last-child {
  margin-bottom: 0;
}

.secret-field label {
  display: block;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  font-size: 14px;
}

.secret-value {
  display: flex;
  align-items: center;
  gap: 8px;
}

.secret-value code {
  background: white;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  color: #495057;
  flex: 1;
  word-break: break-all;
  line-height: 1.6;
}

.secret-code {
  color: #d63384 !important;
  font-weight: 500;
}

@media (max-width: 768px) {
  .form-row,
  .form-row.sms-dispatch-row,
  .sms-point-board {
    grid-template-columns: 1fr;
  }

  .input-with-button {
    flex-direction: column;
  }

  .btn-generate {
    width: 100%;
    justify-content: center;
  }

  .credential-field {
    flex-direction: column;
    align-items: flex-start;
  }

  .credential-field label {
    min-width: auto;
  }

  .credential-value {
    width: 100%;
  }

  .credential-actions {
    flex-direction: column;
  }

  .credential-actions button {
    width: 100%;
    justify-content: center;
  }

  .modal-content {
    max-width: 100%;
    margin: 0;
    border-radius: 12px 12px 0 0;
  }

  .secret-value {
    flex-direction: column;
    align-items: stretch;
  }

  .secret-value .btn-copy {
    width: 100%;
    justify-content: center;
  }
}
</style>
